import React, { Fragment, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { H5, Breadcrumbs } from "../../AbstractElements";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
  CardHeader,
} from "reactstrap";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { createReservation } from "../../api/reservation.api";
import { getVenues } from "../../api/venue.api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { generateTimeOptions, to12Hour } from "../../utils/datetime";

const ReservationCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [venues, setVenues] = useState([]);

  const [recurring, setRecurring] = useState(false);
  const [recurringStartDate, setRecurringStartDate] = useState("");
  const [recurringEndDate, setRecurringEndDate] = useState("");
  const [recurringDays, setRecurringDays] = useState([]);
  const [recurringStartTime, setRecurringStartTime] = useState("");
  const [recurringEndTime, setRecurringEndTime] = useState("");

  const [form, setForm] = useState({
    name: "",
    phoneNo: "",
    email: "",
    status: "pending",
    paymentStatus: "not_applicable",
    type: "special_event",
    purpose: "",
    specialRequest: "",
    venueIds: [],
    bookings: [
      {
        venueId: "",
        date: "",
        startTime: "",
        endTime: "",
      },
    ],
    reservationDate: "",
    reservationStartTime: "",
    reservationEndTime: "",
  });

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await getVenues();
        setVenues(res.data || []);
      } catch (err) {
        toast.error("Failed to load venues");
      }
    };

    fetchVenues();
  }, []);

  const isNonBooking = form.type !== "booking";

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBookingChange = (index, field, value) => {
    setForm((prev) => {
      const bookings = [...prev.bookings];
      bookings[index][field] = value;

      return { ...prev, bookings };
    });
  };

  const addBooking = () => {
    setForm((prev) => ({
      ...prev,
      bookings: [
        ...prev.bookings,
        {
          venueId: "",
          date: "",
          startTime: "",
          endTime: "",
        },
      ],
    }));
  };

  const removeBooking = (index) => {
    setForm((prev) => ({
      ...prev,
      bookings: prev.bookings.filter((_, i) => i !== index),
    }));
  };

  const toggleVenue = (venueId) => {
    setForm((prev) => ({
      ...prev,
      venueIds: prev.venueIds.includes(venueId)
        ? prev.venueIds.filter((v) => v !== venueId)
        : [...prev.venueIds, venueId],
    }));
  };

  const weekdays = [
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
    { value: 0, label: "Sunday" },
  ];

  const toggleRecurringDay = (day) => {
    setRecurringDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const generateRecurringBookings = () => {
    if (!recurringStartDate || !recurringEndDate) {
      throw new Error("Recurring start and end dates are required");
    }
    if (!recurringDays.length) {
      throw new Error("Please select at least one recurring day");
    }
    if (!recurringStartTime || !recurringEndTime) {
      throw new Error("Recurring start and end times are required");
    }
    if (recurringStartTime >= recurringEndTime) {
      throw new Error("Recurring end time must be later than start time");
    }
    if (!form.venueIds.length) {
      throw new Error("Please select at least one venue");
    }
    if (recurringStartDate > recurringEndDate) {
      throw new Error("Recurring end date must be on or after the start date");
    }

    const result = [];
    const cursor = new Date(`${recurringStartDate}T00:00:00Z`);
    const end = new Date(`${recurringEndDate}T00:00:00Z`);

    while (cursor <= end) {
      const day = cursor.getUTCDay();
      if (recurringDays.includes(day)) {
        const date = cursor.toISOString().slice(0, 10);
        form.venueIds.forEach((venueId) => {
          result.push({
            venueId: Number(venueId),
            date,
            startTime: recurringStartTime,
            endTime: recurringEndTime,
          });
        });
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    if (!result.length) {
      throw new Error("No dates match the selected recurring days");
    }

    return result;
  };

  const formatConflictMessage = (conflicts = []) => {
    const lines = conflicts.slice(0, 10).map((conflict) => {
      const venue = venues.find((v) => Number(v.id) === Number(conflict.venueId));
      return `- ${venue?.name || `Venue ${conflict.venueId}`} | ${conflict.date} | ${conflict.startTime} - ${conflict.endTime}`;
    });
    const more = conflicts.length > 10 ? `\n...and ${conflicts.length - 10} more.` : "";
    return `Some selected schedules are already booked:\n\n${lines.join("\n")}${more}\n\nDo you want to proceed anyway?`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isNonBooking) {
        if (!form.venueIds.length) {
          return toast.error("Please select at least one venue");
        }

        if (
          !form.reservationDate ||
          !form.reservationStartTime ||
          !form.reservationEndTime
        ) {
          return toast.error("Date and time are required");
        }
      } else {
        if (recurring) {
          if (!form.venueIds.length) {
            return toast.error("Please select at least one venue");
          }
        } else {
          for (const booking of form.bookings) {
            if (
              !booking.venueId ||
              !booking.date ||
              !booking.startTime ||
              !booking.endTime
            ) {
              return toast.error("Please complete all booking schedules");
            }
            if (booking.startTime >= booking.endTime) {
              return toast.error("End time must be later than start time");
            }
          }
        }
      }

      const requestedBookings = recurring
        ? generateRecurringBookings()
        : form.bookings.map((b) => ({
            ...b,
            venueId: Number(b.venueId),
          }));

      let payload = {
        ...form,
        venueIds: form.venueIds.map(Number),
        bookings: requestedBookings,
        forceProceed: false,
      };

      if (isNonBooking) {
        payload = {
          ...payload,
          name: "-",
          phoneNo: "-",
          email: "-",
          status: "confirmed",
          paymentStatus: "not_applicable",
          specialRequest: "",
        };
      }

      setLoading(true);

      try {
        await createReservation(payload);
      } catch (err) {
        if (err.status === 409 && err.code === "RESERVATION_CONFLICT") {
          const proceed = window.confirm(
            formatConflictMessage(err.data?.conflicts || [])
          );

          if (!proceed) {
            return;
          }

          await createReservation({ ...payload, forceProceed: true });
        } else {
          throw err;
        }
      }

      toast.success("Reservation created successfully");
      navigate("/reservations");
    } catch (err) {
      toast.error(err.message || "Failed to create reservation");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("reservation"))}
        title={titleCase(t("new reservation"))}
        mainTitle={titleCase(t("new reservation"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Card className="mb-4">
              <CardBody>
                <Form innerRef={formRef} onSubmit={handleSubmit}>

                  {/* TYPE (BUTTON SELECTION) */}
                  <FormGroup>
                    <Label>{titleCase(t("reservation type"))}</Label>
                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        color={form.type === "special_event" ? "primary" : "light"}
                        onClick={() =>
                          handleChange("type", "special_event")
                        }
                      >
                        {titleCase(t("special event"))}
                      </Button>

                      <Button
                        type="button"
                        color={form.type === "maintenance" ? "primary" : "light"}
                        onClick={() => handleChange("type", "maintenance")}
                      >
                        {titleCase(t("maintenance"))}
                      </Button>

                      <Button
                        type="button"
                        color={form.type === "booking" ? "primary" : "light"}
                        onClick={() => handleChange("type", "booking")}
                      >
                        {titleCase(t("booking"))}
                      </Button>
                    </div>
                  </FormGroup>

                  {/* COMMON FIELDS */}
                  <FormGroup>
                    <Label>{titleCase(t("purpose"))}</Label>
                    <Input
                      type="text"
                      value={form.purpose}
                      onChange={(e) =>
                        handleChange("purpose", e.target.value)
                      }
                    />
                  </FormGroup>

                  {/* BOOKING ONLY FIELDS */}
                  {!isNonBooking && (
                    <>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("name"))}</Label>
                            <Input
                              type="text"
                              value={form.name}
                              onChange={(e) =>
                                handleChange("name", e.target.value)
                              }
                              required
                            />
                          </FormGroup>
                        </Col>

                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("phone number"))}</Label>
                            <Input
                              type="text"
                              value={form.phoneNo}
                              onChange={(e) =>
                                handleChange("phoneNo", e.target.value)
                              }
                              required
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <FormGroup>
                        <Label>{titleCase(t("email"))}</Label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) =>
                            handleChange("email", e.target.value)
                          }
                          required
                        />
                      </FormGroup>

                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("status"))}</Label>
                            <Input
                              type="select"
                              value={form.status}
                              onChange={(e) =>
                                handleChange("status", e.target.value)
                              }
                            >
                              <option value="pending">
                                {titleCase(t("pending"))}
                              </option>
                              {/* <option value="approved">
                                {titleCase(t("approved"))}
                              </option> */}
                              <option value="confirmed">
                                {titleCase(t("confirmed"))}
                              </option>
                              <option value="cancelled">
                                {titleCase(t("cancelled"))}
                              </option>
                            </Input>
                          </FormGroup>
                        </Col>

                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("payment status"))}</Label>
                            <Input
                              type="select"
                              value={form.paymentStatus}
                              onChange={(e) =>
                                handleChange(
                                  "paymentStatus",
                                  e.target.value
                                )
                              }
                            >
                              <option value="pending">
                                {titleCase(t("pending"))}
                              </option>
                              <option value="paid">
                                {titleCase(t("paid"))}
                              </option>
                              <option value="not_applicable">
                                {titleCase(t("not applicable"))}
                              </option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  )}

                  {isNonBooking ? (
                    <Card className="mt-3">
                      <CardHeader>{titleCase(t("reservation schedule"))}</CardHeader>

                      <CardBody>
                        <Row className="mb-3">
                          <Col md="4">
                            <Label>{titleCase(t("date"))}</Label>
                            <Input
                              type="date"
                              value={form.reservationDate}
                              onChange={(e) => handleChange("reservationDate", e.target.value)}
                            />
                          </Col>

                          <Col md="4">
                            <Label>{titleCase(t("start time"))}</Label>
                            <Input
                              type="select"
                              value={form.reservationStartTime}
                              onChange={(e) =>
                                handleChange("reservationStartTime", e.target.value)
                              }
                            >
                              <option value="" />
                              {generateTimeOptions().map((time) => (
                                <option key={time} value={time}>
                                  {to12Hour(time)}
                                </option>
                              ))}
                            </Input>
                          </Col>

                          <Col md="4">
                            <Label>{titleCase(t("end time"))}</Label>
                            <Input
                              type="select"
                              value={form.reservationEndTime}
                              onChange={(e) => handleChange("reservationEndTime", e.target.value)}
                            >
                              <option value="" />
                              {generateTimeOptions().map((time) => (
                                <option key={time} value={time}>
                                  {to12Hour(time)}
                                </option>
                              ))}
                            </Input>
                          </Col>
                        </Row>
                        <Label className="mb-2">{titleCase(t("venues"))}</Label>
                        <Row>
                          {venues.map((venue) => (
                            <Col md="4" key={venue.id} className="mb-2">
                              <div className="checkbox checkbox-primary">
                                <Input
                                  id={`venue-${venue.id}`}
                                  type="checkbox"
                                  checked={form.venueIds.includes(venue.id)}
                                  onChange={() => toggleVenue(venue.id)}
                                />
                                <Label for={`venue-${venue.id}`}>{venue.name}</Label>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </CardBody>
                    </Card>
                  ) : (
                    <>
                      <Card className="mt-3">
                        <CardHeader className="d-flex justify-content-between align-items-center">
                          <span>{titleCase(t("recurring booking"))}</span>
                          <div className="form-check form-switch mb-0">
                            <Input
                              type="switch"
                              checked={recurring}
                              onChange={(e) => setRecurring(e.target.checked)}
                              id="recurring-booking"
                            />
                            <Label check for="recurring-booking" className="mb-0">
                              {recurring ? "Yes" : "No"}
                            </Label>
                          </div>
                        </CardHeader>

                        {recurring && (
                          <CardBody>
                            <Row>
                              <Col md="6">
                                <FormGroup>
                                  <Label>{titleCase(t("recurring start date"))}</Label>
                                  <Input
                                    type="date"
                                    value={recurringStartDate}
                                    onChange={(e) => setRecurringStartDate(e.target.value)}
                                    required={recurring}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md="6">
                                <FormGroup>
                                  <Label>{titleCase(t("recurring end date"))}</Label>
                                  <Input
                                    type="date"
                                    value={recurringEndDate}
                                    onChange={(e) => setRecurringEndDate(e.target.value)}
                                    required={recurring}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>

                            <FormGroup>
                              <Label>{titleCase(t("days of week"))}</Label>
                              <div className="d-flex flex-wrap gap-3">
                                {weekdays.map((day) => (
                                  <div className="checkbox checkbox-primary" key={day.value}>
                                    <Input
                                      id={`recurring-day-${day.value}`}
                                      type="checkbox"
                                      checked={recurringDays.includes(day.value)}
                                      onChange={() => toggleRecurringDay(day.value)}
                                    />
                                    <Label for={`recurring-day-${day.value}`}>{day.label}</Label>
                                  </div>
                                ))}
                              </div>
                            </FormGroup>

                            <Row>
                              <Col md="6">
                                <FormGroup>
                                  <Label>{titleCase(t("start time"))}</Label>
                                  <Input
                                    type="select"
                                    value={recurringStartTime}
                                    onChange={(e) => setRecurringStartTime(e.target.value)}
                                    required={recurring}
                                  >
                                    <option value="" />
                                    {generateTimeOptions().map((time) => (
                                      <option key={time} value={time}>{to12Hour(time)}</option>
                                    ))}
                                  </Input>
                                </FormGroup>
                              </Col>
                              <Col md="6">
                                <FormGroup>
                                  <Label>{titleCase(t("end time"))}</Label>
                                  <Input
                                    type="select"
                                    value={recurringEndTime}
                                    onChange={(e) => setRecurringEndTime(e.target.value)}
                                    required={recurring}
                                  >
                                    <option value="" />
                                    {generateTimeOptions().map((time) => (
                                      <option key={time} value={time}>{to12Hour(time)}</option>
                                    ))}
                                  </Input>
                                </FormGroup>
                              </Col>
                            </Row>

                            <FormGroup>
                              <Label className="mb-2">{titleCase(t("venues"))}</Label>
                              <Row>
                                {venues.map((venue) => (
                                  <Col md="4" key={venue.id} className="mb-2">
                                    <div className="checkbox checkbox-primary">
                                      <Input
                                        id={`recurring-venue-${venue.id}`}
                                        type="checkbox"
                                        checked={form.venueIds.includes(venue.id)}
                                        onChange={() => toggleVenue(venue.id)}
                                      />
                                      <Label for={`recurring-venue-${venue.id}`}>{venue.name}</Label>
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            </FormGroup>

                            <small className="text-muted">
                              Each matching date will be created as an individual reservation.
                            </small>
                          </CardBody>
                        )}
                      </Card>

                      {!recurring && (
                      <Card className="mt-3">
                      <CardHeader className="d-flex justify-content-between align-items-center">
                        <span>{titleCase(t("reservation schedule"))}</span>

                        <Button
                          size="sm"
                          color="primary"
                          type="button"
                          onClick={addBooking}
                        >
                          {titleCase(t("add venue"))}
                        </Button>
                      </CardHeader>

                      <CardBody>
                        {form.bookings.map((booking, index) => (
                          <Row key={index} className="align-items-end mb-3">
                            <Col md="3">
                              <Label>{titleCase(t("venue"))}</Label>
                              <Input
                                type="select"
                                value={booking.venueId}
                                onChange={(e) =>
                                  handleBookingChange(index, "venueId", e.target.value)
                                }
                              >
                                <option value="" />
                                {venues.map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {v.name}
                                  </option>
                                ))}
                              </Input>
                            </Col>

                            <Col md="3">
                              <Label>{titleCase(t("date"))}</Label>
                              <Input
                                type="date"
                                value={booking.date}
                                onChange={(e) =>
                                  handleBookingChange(index, "date", e.target.value)
                                }
                              />
                            </Col>

                            <Col md="2">
                              <Label>{titleCase(t("start"))}</Label>
                              <Input
                                type="select"
                                value={booking.startTime}
                                onChange={(e) =>
                                  handleBookingChange(index, "startTime", e.target.value)
                                }
                              >
                                <option value="" />
                                {generateTimeOptions().map((time) => (
                                  <option key={time} value={time}>
                                    {to12Hour(time)}
                                  </option>
                                ))}
                              </Input>
                            </Col>

                            <Col md="2">
                              <Label>{titleCase(t("end"))}</Label>
                              <Input
                                type="select"
                                value={booking.endTime}
                                onChange={(e) =>
                                  handleBookingChange(index, "endTime", e.target.value)
                                }
                              >
                                <option value="" />
                                {generateTimeOptions().map((time) => (
                                  <option key={time} value={time}>
                                    {to12Hour(time)}
                                  </option>
                                ))}
                              </Input>
                            </Col>

                            <Col md="2">
                              <Button
                                color="danger"
                                outline
                                type="button"
                                disabled={form.bookings.length === 1}
                                onClick={() => removeBooking(index)}
                              >
                                {titleCase(t("remove"))}
                              </Button>
                            </Col>
                          </Row>
                        ))}
                      </CardBody>
                    </Card>
                      )}
                    </>
                  )}

                  {/* SPECIAL REQUEST */}
                  {!isNonBooking && (
                    <FormGroup>
                      <Label>{titleCase(t("special request"))}</Label>
                      <Input
                        type="textarea"
                        value={form.specialRequest}
                        onChange={(e) =>
                          handleChange("specialRequest", e.target.value)
                        }
                      />
                    </FormGroup>
                  )}
                </Form>
              </CardBody>
            </Card>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ReservationCreate;